import { PortfolioAllocation, PortfolioAllocationDataPoint } from '@models';
import {
  nestPortfolioDataPoints,
  sortPortfolioDataPoints,
} from '@products/portfolio/portfolio.utils';
import { ImpactFocusDTO, PortfolioComponentId, PortfolioDTO } from '@types';
import { Logger } from '@utils/logger';
import { formatPercentage } from '@utils/text/number-utils';
import { MapperFactory } from '../mapper-factory';
import { getFirstNonEmptyProp, parseStdNumber, parseYN } from '../mapper.utils';
import { Mapper, MapperParams } from '../type.mapper';

const logger = Logger.getLogger('ImpactFocusMapper');

export const isImpactFocus = (point: ImpactFocusDTO): boolean =>
  point.calcnamestd === 'PCT_TOTAL';

/**
 * example fund:
 * https://staging.ftsites.com/en-gb/our-funds/price-and-performance/
 * products/36605/SGR/ftgf-martin-currie-improving-society-fund/IE000ZMRJWS1#impact-focus
 */
export const mapImpactFocus = (
  data: ImpactFocusDTO[],
  componentId: PortfolioComponentId,
  baseLabelKey: string
): PortfolioAllocation => ({
  componentId,
  caveatComponentId: PortfolioComponentId.IMPACT_FOCUS,
  baseLabelKey,
  asOfDate: getFirstNonEmptyProp(data, 'asofdate'),
  calculationType: getFirstNonEmptyProp(data, 'calcname'),
  updateFrequency: getFirstNonEmptyProp(data, 'frequency'),
  data: data.sort(sortPortfolioDataPoints).map(
    (obj: ImpactFocusDTO): PortfolioAllocationDataPoint => ({
      label: obj.sectorname,
      breakdown: formatPercentage(obj.brkdwnpct),
      breakdownStd: parseStdNumber(obj.brkdwnpctstd),
    })
  ),
});

/**
 * Static config class for the different types of Impact focus
 * config class based on this https://stackoverflow.com/a/51398471
 */
export class ImpactFocusConfig {
  // private to disallow creating other instances of this type
  private constructor(
    public readonly componentId: PortfolioComponentId,
    // used for labels, caveats etc e.g. "top-ten-holding-money-funds"
    public readonly baseLabelKey: string,
    // used to convert filtered data into a PortfolioAllocation object
    public readonly mapFn: (
      data: ImpactFocusDTO[],
      componentId: PortfolioComponentId,
      baseLabelKey: string,
      mapperParams?: MapperParams
    ) => PortfolioAllocation,
    public readonly filterFn?: (point: ImpactFocusDTO) => boolean
  ) {}
  // configs

  // tslint:disable-next-line:max-line-length
  // https://staging.ftsites.com/en-gb/our-funds/price-and-performance/products/36605/SGR/ftgf-martin-currie-improving-society-fund/IE000ZMRJWS1#impact-focus
  static readonly IMPACT_FOCUS = new ImpactFocusConfig(
    PortfolioComponentId.IMPACT_FOCUS,
    'impact-focus',
    mapImpactFocus
  );

  static allConfigs: ImpactFocusConfig[] = [ImpactFocusConfig.IMPACT_FOCUS];

  // returns matching config or undefined
  static getConfig(componentId: PortfolioComponentId): ImpactFocusConfig {
    return this.allConfigs.find(
      (config: ImpactFocusConfig): boolean => config.componentId === componentId
    ); // just match on key
  }

  // e.g. console.log(ImpactFocusConfig.IMPACT_FOCUS) prints 'IMPACT_FOCUS'
  toString() {
    return this.componentId;
  }
}

export interface AllocationBucket {
  config: ImpactFocusConfig;
  data: ImpactFocusDTO[];
}

export const reduceImpactFocusData = (
  buckets: { [id: string]: AllocationBucket },
  point: ImpactFocusDTO
): { [id: string]: AllocationBucket } => {
  // 1. see which allocation type filter passes for this row
  let config: ImpactFocusConfig = ImpactFocusConfig.allConfigs.find(
    (c: ImpactFocusConfig): boolean => c.filterFn && c.filterFn(point)
  );
  if (!config) {
    // set to default config if it doesn't pass any filters
    config = ImpactFocusConfig.IMPACT_FOCUS;
  }

  // 2. create new bucket if it doesn't already exist
  if (!buckets[config.componentId]) {
    buckets[config.componentId] = { config, data: [] };
  }

  // 3. add row to bucket
  buckets[config.componentId].data.push(point);

  return buckets;
};

export const mapImpactFocusBuckets = (
  bucket: AllocationBucket
): PortfolioAllocation =>
  bucket.config.mapFn(
    bucket.data,
    bucket.config.componentId,
    bucket.config.baseLabelKey
  );

export class ImpactFocusMapper extends Mapper<PortfolioAllocation[]> {
  constructor(mapperParams: MapperParams, mapperFactory: MapperFactory) {
    super(mapperParams, mapperFactory);
  }

  toDomain(portfolioDto: PortfolioDTO): PortfolioAllocation[] {
    const data: ImpactFocusDTO[] = portfolioDto.impactfocus;

    // if no data, return null
    if (!data || data.length === 0) {
      return null;
    }

    const buckets: {
      [id: string]: AllocationBucket;
    } = data.reduce(reduceImpactFocusData, {});
    logger.debug('impact focus buckets', buckets);

    // 2. map buckets into allocation objects
    const allocations: PortfolioAllocation[] = Object.values(buckets).map(
      mapImpactFocusBuckets
    );
    logger.debug('ImpactFocus allocations', allocations);

    return allocations;
  }
}
